import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const TABS = [
  { id: "overview", label: "Overview", icon: "fa-gauge", testid: "admin-tab-overview" },
  { id: "pending", label: "Pending", icon: "fa-clock-rotate-left", testid: "admin-tab-pending" },
  { id: "research", label: "Deep Research", icon: "fa-magnifying-glass", testid: "admin-tab-research" },
  { id: "news", label: "Newsroom", icon: "fa-newspaper", testid: ADMIN.tabNews },
  { id: "videos", label: "Videos", icon: "fa-video", testid: ADMIN.tabVideos },
  { id: "articles", label: "Articles", icon: "fa-pen", testid: ADMIN.tabArticles },
  { id: "companies", label: "Companies", icon: "fa-building", testid: ADMIN.tabCompanies },
  { id: "gallery", label: "Gallery", icon: "fa-images", testid: ADMIN.tabGallery },
  { id: "leads", label: "Leads", icon: "fa-inbox", testid: ADMIN.tabLeads },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const nav = useNavigate();
  const email = localStorage.getItem("nn_admin_email");

  useEffect(() => {
    if (!localStorage.getItem("nn_admin_token")) { nav("/admin/login"); return; }
    client.get("/admin/stats").then(r => setStats(r.data)).catch(() => nav("/admin/login"));
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("nn_admin_token");
    localStorage.removeItem("nn_admin_email");
    nav("/admin/login");
  };

  return (
    <main data-testid={ADMIN.dashboard} className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-10">
        <div>
          <div className="eyebrow mb-2">Admin · CMS</div>
          <h1 className="serif-title text-4xl text-[#0a1628]">Office of Nitin Narang</h1>
          <p className="text-[13px] text-[#6b6b6b] mt-2">Signed in as {email}</p>
        </div>
        <button data-testid={ADMIN.logout} onClick={logout} className="btn btn-ghost">
          <i className="fa-solid fa-arrow-right-from-bracket"></i> Sign out
        </button>
      </div>

      <div className="flex flex-wrap gap-2 border-b hairline pb-4 mb-8">
        {TABS.map(t => (
          <button key={t.id} data-testid={t.testid} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-[12px] tracking-[0.14em] uppercase border transition-all flex items-center gap-2 ${
              tab === t.id ? "bg-[#0a1628] text-[#faf8f3] border-[#0a1628]" : "border-[#d9d4c7] text-[#2c2c2c] hover:border-[#0a1628]"
            }`}>
            <i className={`fa-solid ${t.icon}`}></i> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview stats={stats} setTab={setTab} />}
      {tab === "pending" && <PendingAdmin />}
      {tab === "research" && <DeepResearch />}
      {tab === "news" && <NewsAdmin />}
      {tab === "videos" && <VideosAdmin />}
      {tab === "articles" && <ArticlesAdmin />}
      {tab === "companies" && <CompaniesAdmin />}
      {tab === "gallery" && <GalleryAdmin />}
      {tab === "leads" && <LeadsAdmin />}
    </main>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="border hairline bg-white p-6">
      <div className="text-[11px] tracking-[0.2em] uppercase text-[#6b6b6b] mb-2">{label}</div>
      <div className="font-display text-4xl text-[#0a1628]">{value ?? "—"}</div>
      {hint && <div className="text-[12px] text-[#b8975d] mt-2">{hint}</div>}
    </div>
  );
}

function Overview({ stats, setTab }) {
  return (
    <div className="space-y-8">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard label="News (approved)" value={stats?.news_approved} hint={stats?.news_pending ? `${stats.news_pending} pending` : null}/>
        <StatCard label="Videos (approved)" value={stats?.videos_approved} hint={stats?.videos_pending ? `${stats.videos_pending} pending` : null}/>
        <StatCard label="Articles (published)" value={stats?.articles_published} hint={stats?.articles_draft ? `${stats.articles_draft} draft` : null}/>
        <StatCard label="Leads" value={stats?.leads_total} hint={stats?.leads_new ? `${stats.leads_new} new` : null}/>
        <StatCard label="Companies" value={stats?.companies}/>
        <StatCard label="Gallery" value={stats?.gallery}/>
      </div>

      <div className="border hairline bg-white p-8">
        <div className="eyebrow mb-3">Quick actions</div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setTab("news")} className="btn">+ News</button>
          <button onClick={() => setTab("videos")} className="btn">+ Video</button>
          <button onClick={() => setTab("articles")} className="btn">+ Article</button>
          <button onClick={() => setTab("leads")} className="btn btn-ghost">View Leads</button>
        </div>
      </div>
    </div>
  );
}

function DeepResearch() {
  const [query, setQuery] = useState("");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async (e) => {
    e.preventDefault();
    setError(""); setResult(null); setLoading(true);
    try {
      const r = await client.post("/admin/ai/deep-research", { query, context });
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Research failed");
    } finally { setLoading(false); }
  };

  const importNews = async (n) => {
    try {
      await client.post("/admin/news", {
        title: n.title, source: n.source, url: n.url,
        date: n.date || new Date().toISOString().slice(0,10),
        category: n.category || "Chess",
        excerpt: n.excerpt || "", image: null, status: "pending",
      });
      alert("Imported to Newsroom (pending review).");
    } catch (err) { alert(err.response?.data?.detail || "Import failed (URL may already exist)."); }
  };

  const importVideo = async (v) => {
    let yid = v.youtube_id_or_url || "";
    try {
      if (yid.includes("youtu")) {
        const u = new URL(yid);
        yid = u.searchParams.get("v") || u.pathname.split("/").pop();
      }
    } catch {}
    try {
      await client.post("/admin/videos", {
        youtube_id: yid, title: v.title, channel: v.channel || "",
        date: v.date || "", category: v.category || "Interviews",
        description: v.description || "", status: "pending",
      });
      alert("Imported to Videos (pending review).");
    } catch (err) { alert(err.response?.data?.detail || "Import failed."); }
  };

  const importPhoto = async (p) => {
    try {
      await client.post("/admin/gallery", {
        title: p.title, image: p.image_url, category: p.category || "AICF", caption: p.caption || "",
      });
      alert("Imported to Gallery.");
    } catch (err) { alert(err.response?.data?.detail || "Import failed."); }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={run} className="border hairline bg-white p-6 space-y-4">
        <div className="eyebrow">Deep web research</div>
        <p className="text-[13px] text-[#6b6b6b]">
          Use AI to consolidate publicly available news, videos, photos and quotes about a specific topic into a structured brief — ready to import into Newsroom, Videos and Gallery.
        </p>
        <input className="form-input" placeholder='e.g. "Nitin Narang and HearClear partnerships"' value={query} onChange={(e) => setQuery(e.target.value)} required />
        <input className="form-input" placeholder='Optional context — year, geography, company, person' value={context} onChange={(e) => setContext(e.target.value)} />
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-[#6b6b6b]">Powered by Claude Sonnet 4.6</p>
          <button disabled={loading} className="btn">
            {loading ? "Researching…" : "Run Research"} <i className="fa-solid fa-magnifying-glass"></i>
          </button>
        </div>
        {error && <div className="text-[13px] text-red-700 border border-red-300 bg-red-50 p-3">{error}</div>}
      </form>

      {result && (
        <div className="space-y-6">
          {result.summary && (
            <div className="border hairline bg-white p-6">
              <div className="eyebrow mb-2">Summary</div>
              <p className="text-[15px] text-[#2c2c2c] leading-relaxed">{result.summary}</p>
            </div>
          )}

          {result.news_items?.length > 0 && (
            <div className="border hairline bg-white p-6">
              <div className="eyebrow mb-4">News items ({result.news_items.length})</div>
              <div className="space-y-3">
                {result.news_items.map((n, i) => (
                  <div key={i} className="border hairline p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="text-[11px] tracking-[0.18em] uppercase text-[#b8975d] mb-1">{n.category} · {n.source} · {n.date}</div>
                        <div className="font-display text-lg text-[#0a1628]">{n.title}</div>
                        <p className="text-[13px] text-[#4a4a4a] mt-2">{n.excerpt}</p>
                        {n.url && <a href={n.url} target="_blank" rel="noreferrer" className="text-[11px] tracking-[0.16em] uppercase link-underline mt-2 inline-block">Source ↗</a>}
                      </div>
                      <button onClick={() => importNews(n)} className="btn text-[11px] px-3 py-2 shrink-0">+ Import</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.videos?.length > 0 && (
            <div className="border hairline bg-white p-6">
              <div className="eyebrow mb-4">Videos ({result.videos.length})</div>
              <div className="space-y-3">
                {result.videos.map((v, i) => (
                  <div key={i} className="border hairline p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="text-[11px] tracking-[0.18em] uppercase text-[#b8975d] mb-1">{v.category} · {v.channel}</div>
                        <div className="font-display text-lg text-[#0a1628]">{v.title}</div>
                        <p className="text-[13px] text-[#4a4a4a] mt-2">{v.description}</p>
                        {v.youtube_id_or_url && <div className="text-[11px] text-[#6b6b6b] mt-2 break-all">{v.youtube_id_or_url}</div>}
                      </div>
                      <button onClick={() => importVideo(v)} className="btn text-[11px] px-3 py-2 shrink-0">+ Import</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.photos?.length > 0 && (
            <div className="border hairline bg-white p-6">
              <div className="eyebrow mb-4">Photos ({result.photos.length})</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {result.photos.map((p, i) => (
                  <div key={i} className="border hairline">
                    {p.image_url && <div className="aspect-video bg-[#ece7da]"><img src={p.image_url} alt={p.title} className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.style.display='none'}}/></div>}
                    <div className="p-3">
                      <div className="text-[11px] tracking-[0.18em] uppercase text-[#b8975d] mb-1">{p.category}</div>
                      <div className="font-display text-[14px] text-[#0a1628] mb-2">{p.title}</div>
                      <div className="flex gap-2">
                        <button onClick={() => importPhoto(p)} className="btn text-[10px] px-3 py-1.5">+ Add</button>
                        {p.source_page_url && <a href={p.source_page_url} target="_blank" rel="noreferrer" className="text-[10px] tracking-[0.14em] uppercase link-underline py-1.5">Source</a>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.quotes?.length > 0 && (
            <div className="border hairline bg-white p-6">
              <div className="eyebrow mb-4">Quotes ({result.quotes.length})</div>
              <div className="space-y-3">
                {result.quotes.map((q, i) => (
                  <div key={i} className="border-l-2 border-[#b8975d] pl-4">
                    <p className="font-display italic text-[#0a1628] text-[16px]">"{q.quote}"</p>
                    <div className="text-[11px] tracking-[0.16em] uppercase text-[#6b6b6b] mt-1">— {q.context}</div>
                    {q.source_url && <a href={q.source_url} target="_blank" rel="noreferrer" className="text-[10px] tracking-[0.14em] uppercase link-underline mt-1 inline-block">Source ↗</a>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.followups?.length > 0 && (
            <div className="border hairline bg-white p-6">
              <div className="eyebrow mb-4">Suggested follow-up queries</div>
              <ul className="space-y-2">
                {result.followups.map((f, i) => (
                  <li key={i} className="text-[14px]">
                    <div className="text-[#0a1628]">{f.open_question}</div>
                    <button onClick={() => { setQuery(f.suggested_query || f.open_question); setResult(null); }} className="text-[11px] tracking-[0.16em] uppercase link-underline mt-1">Run "{f.suggested_query}"</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NewsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", source: "", url: "", date: "", category: "Chess", excerpt: "", image: "", status: "approved" });
  const [filter, setFilter] = useState("all");

  const load = () => {
    const p = filter === "all" ? {} : { status: filter };
    client.get("/admin/news", { params: p }).then(r => setItems(r.data.items));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await client.post("/admin/news", form);
      setForm({ title: "", source: "", url: "", date: "", category: "Chess", excerpt: "", image: "", status: "approved" });
      load();
    } catch (err) { alert(err.response?.data?.detail || "Error"); }
  };

  const setStatus = async (id, status) => { await client.patch(`/admin/news/${id}`, { status }); load(); };
  const del = async (id) => { if (confirm("Delete?")) { await client.delete(`/admin/news/${id}`); load(); } };

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5">
        <form onSubmit={submit} className="border hairline bg-white p-6 space-y-4 sticky top-24">
          <div className="eyebrow">Add news article</div>
          <input className="form-input" placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required />
          <div className="grid grid-cols-2 gap-3">
            <input className="form-input" placeholder="Source (e.g. The Hindu)" value={form.source} onChange={(e) => setForm({...form, source: e.target.value})} required />
            <input className="form-input" type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} required />
          </div>
          <input className="form-input" placeholder="Original URL" value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} required />
          <div className="grid grid-cols-2 gap-3">
            <select className="form-input" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}>
              {["Chess","AICF","FIDE","Indian Chess","Entrepreneurship","Investments","Companies","Interviews"].map(c => <option key={c}>{c}</option>)}
            </select>
            <select className="form-input" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
              <option value="approved">Approved (live)</option>
              <option value="pending">Pending review</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <textarea className="form-input" rows="3" placeholder="Short excerpt" value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})} />
          <input className="form-input" placeholder="Image URL (optional)" value={form.image} onChange={(e) => setForm({...form, image: e.target.value})} />
          <button className="btn w-full justify-center">Save Article</button>
        </form>
      </div>
      <div className="lg:col-span-7 space-y-3">
        <div className="flex gap-2 mb-3">
          {["all","approved","pending","rejected"].map(s => (
            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-[11px] tracking-[0.16em] uppercase border ${filter===s?"bg-[#0a1628] text-[#faf8f3] border-[#0a1628]":"border-[#d9d4c7]"}`}>{s}</button>
          ))}
        </div>
        {items.map(n => (
          <div key={n.id} className="border hairline bg-white p-5">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="font-display text-lg text-[#0a1628] flex-1">{n.title}</div>
              <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-1 ${
                n.status === "approved" ? "bg-[#0a1628] text-[#faf8f3]" : n.status === "pending" ? "bg-[#faf3e2] text-[#0a1628]" : "bg-red-50 text-red-700"
              }`}>{n.status}</span>
            </div>
            <div className="text-[11px] tracking-[0.16em] uppercase text-[#6b6b6b] mb-2">{n.source} · {n.category} · {n.date}</div>
            <p className="text-[13px] text-[#4a4a4a] line-clamp-2 mb-3">{n.excerpt}</p>
            <div className="flex flex-wrap gap-2 text-[11px] tracking-[0.14em] uppercase">
              {n.status !== "approved" && <button onClick={() => setStatus(n.id, "approved")} className="px-3 py-1.5 border border-[#0a1628] hover:bg-[#0a1628] hover:text-[#faf8f3]">Approve</button>}
              {n.status !== "rejected" && <button onClick={() => setStatus(n.id, "rejected")} className="px-3 py-1.5 border hairline">Reject</button>}
              <a href={n.url} target="_blank" rel="noreferrer" className="px-3 py-1.5 border hairline">Source ↗</a>
              <button onClick={() => del(n.id)} className="px-3 py-1.5 border hairline text-red-700">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VideosAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ youtube_id: "", title: "", channel: "", date: "", category: "Interviews", description: "", status: "approved" });
  const load = () => client.get("/admin/videos").then(r => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const parseId = (val) => {
    try {
      if (val.includes("youtu")) {
        const url = new URL(val);
        return url.searchParams.get("v") || url.pathname.split("/").pop();
      }
    } catch {}
    return val;
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await client.post("/admin/videos", { ...form, youtube_id: parseId(form.youtube_id) });
      setForm({ youtube_id: "", title: "", channel: "", date: "", category: "Interviews", description: "", status: "approved" });
      load();
    } catch (err) { alert(err.response?.data?.detail || "Error"); }
  };

  const setStatus = async (id, status) => { await client.patch(`/admin/videos/${id}`, { status }); load(); };
  const del = async (id) => { if (confirm("Delete?")) { await client.delete(`/admin/videos/${id}`); load(); } };

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5">
        <form onSubmit={submit} className="border hairline bg-white p-6 space-y-4 sticky top-24">
          <div className="eyebrow">Add YouTube video</div>
          <input className="form-input" placeholder="YouTube URL or video id" value={form.youtube_id} onChange={(e) => setForm({...form, youtube_id: e.target.value})} required />
          <input className="form-input" placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required />
          <div className="grid grid-cols-2 gap-3">
            <input className="form-input" placeholder="Channel" value={form.channel} onChange={(e) => setForm({...form, channel: e.target.value})} />
            <input className="form-input" type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select className="form-input" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}>
              {["Interviews","Chess events","AICF announcements","Speeches","Entrepreneurship","Media bytes"].map(c => <option key={c}>{c}</option>)}
            </select>
            <select className="form-input" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
            </select>
          </div>
          <textarea className="form-input" rows="3" placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}/>
          <button className="btn w-full justify-center">Save Video</button>
        </form>
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
        {items.map(v => (
          <div key={v.id} className="border hairline bg-white">
            <div className="aspect-video bg-[#ece7da]">
              <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover"/>
            </div>
            <div className="p-4">
              <div className="eyebrow text-[#b8975d] mb-1">{v.category}</div>
              <div className="font-display text-[15px] text-[#0a1628] mb-2 leading-snug">{v.title}</div>
              <div className="text-[11px] text-[#6b6b6b] mb-3">{v.channel} · {v.status}</div>
              <div className="flex flex-wrap gap-2 text-[10px] tracking-[0.14em] uppercase">
                {v.status !== "approved" && <button onClick={() => setStatus(v.id, "approved")} className="px-2 py-1 border border-[#0a1628]">Approve</button>}
                <a href={`https://youtu.be/${v.youtube_id}`} target="_blank" rel="noreferrer" className="px-2 py-1 border hairline">Watch ↗</a>
                <button onClick={() => del(v.id)} className="px-2 py-1 border hairline text-red-700">Del</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArticlesAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ slug: "", title: "", category: "Leadership", date: new Date().toISOString().slice(0,10), excerpt: "", body: "", status: "draft" });
  const [editing, setEditing] = useState(null);
  const load = () => client.get("/admin/articles").then(r => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const reset = () => { setForm({ slug: "", title: "", category: "Leadership", date: new Date().toISOString().slice(0,10), excerpt: "", body: "", status: "draft" }); setEditing(null); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await client.patch(`/admin/articles/${editing}`, form);
      } else {
        await client.post("/admin/articles", form);
      }
      reset(); load();
    } catch (err) { alert(err.response?.data?.detail || "Error"); }
  };
  const edit = (a) => { setForm(a); setEditing(a.slug); window.scrollTo({top:0, behavior:"smooth"}); };
  const del = async (slug) => { if (confirm("Delete?")) { await client.delete(`/admin/articles/${slug}`); load(); } };

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5">
        <form onSubmit={submit} className="border hairline bg-white p-6 space-y-4 sticky top-24">
          <div className="flex items-center justify-between">
            <div className="eyebrow">{editing ? "Edit article" : "New article"}</div>
            {editing && <button type="button" onClick={reset} className="text-[11px] tracking-[0.18em] uppercase">+ New</button>}
          </div>
          <input className="form-input" placeholder="Slug (e.g. founders-pace)" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} required disabled={!!editing}/>
          <input className="form-input" placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required />
          <div className="grid grid-cols-2 gap-3">
            <select className="form-input" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}>
              {["Chess ecosystem","Sports governance","Entrepreneurship","Investing","Leadership","Youth development","India growth story"].map(c => <option key={c}>{c}</option>)}
            </select>
            <input type="date" className="form-input" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} required />
          </div>
          <select className="form-input" value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
          <textarea className="form-input" rows="3" placeholder="Excerpt (1–2 sentences)" value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})} required />
          <textarea className="form-input" rows="10" placeholder="Article body (use blank line between paragraphs)" value={form.body} onChange={(e) => setForm({...form, body: e.target.value})} required />
          <button className="btn w-full justify-center">{editing ? "Update" : "Save"} Article</button>
        </form>
      </div>
      <div className="lg:col-span-7 space-y-3">
        {items.map(a => (
          <div key={a.slug} className="border hairline bg-white p-5">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="eyebrow text-[#b8975d] mb-1">{a.category}</div>
                <div className="font-display text-lg text-[#0a1628]">{a.title}</div>
                <div className="text-[11px] text-[#6b6b6b] mt-1">{a.slug} · {a.date}</div>
              </div>
              <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-1 ${a.status === "published" ? "bg-[#0a1628] text-[#faf8f3]" : "bg-[#faf3e2]"}`}>{a.status}</span>
            </div>
            <div className="flex gap-2 mt-3 text-[11px] tracking-[0.14em] uppercase">
              <button onClick={() => edit(a)} className="px-3 py-1.5 border hairline">Edit</button>
              <button onClick={() => del(a.slug)} className="px-3 py-1.5 border hairline text-red-700">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompaniesAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => { client.get("/companies").then(r => setItems(r.data.items)); }, []);
  const [editing, setEditing] = useState(null);

  if (editing) {
    return (
      <CompanyEdit
        slug={editing}
        onDone={() => { setEditing(null); client.get("/companies").then(r => setItems(r.data.items)); }}
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="eyebrow mb-2">Companies (4 ventures)</div>
      {items.map(c => (
        <div key={c.slug} className="border hairline bg-white p-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-display text-lg text-[#0a1628]">{c.name}</div>
            <div className="text-[12px] text-[#6b6b6b]">{c.role} · {c.sector}</div>
          </div>
          <div className="flex gap-2 text-[11px] tracking-[0.14em] uppercase">
            <button onClick={() => setEditing(c.slug)} className="px-3 py-1.5 border hairline">Edit</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function CompanyEdit({ slug, onDone }) {
  const [data, setData] = useState(null);
  useEffect(() => { client.get(`/companies/${slug}`).then(r => setData(r.data)); }, [slug]);
  if (!data) return <div className="p-6">Loading…</div>;

  const save = async (e) => {
    e.preventDefault();
    await client.patch(`/admin/companies/${slug}`, data);
    onDone();
  };

  return (
    <form onSubmit={save} className="border hairline bg-white p-6 space-y-4 max-w-3xl">
      <button type="button" onClick={onDone} className="text-[11px] tracking-[0.18em] uppercase link-underline mb-2">← Back</button>
      <input className="form-input" value={data.name} onChange={(e) => setData({...data, name: e.target.value})} />
      <input className="form-input" placeholder="Role" value={data.role} onChange={(e) => setData({...data, role: e.target.value})} />
      <input className="form-input" placeholder="Sector" value={data.sector} onChange={(e) => setData({...data, sector: e.target.value})} />
      <input className="form-input" placeholder="Founded" value={data.founded || ""} onChange={(e) => setData({...data, founded: e.target.value})} />
      <input className="form-input" placeholder="Website URL" value={data.website || ""} onChange={(e) => setData({...data, website: e.target.value})} />
      <input className="form-input" placeholder="Logo URL" value={data.logo || ""} onChange={(e) => setData({...data, logo: e.target.value})} />
      <input className="form-input" placeholder="Tagline" value={data.tagline || ""} onChange={(e) => setData({...data, tagline: e.target.value})} />
      <textarea className="form-input" rows="6" placeholder="Description" value={data.description || ""} onChange={(e) => setData({...data, description: e.target.value})} />
      <textarea className="form-input" rows="4" placeholder="Milestones (one per line)" value={(data.milestones||[]).join("\n")} onChange={(e) => setData({...data, milestones: e.target.value.split("\n").filter(Boolean)})} />
      <button className="btn">Save Company</button>
    </form>
  );
}

function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", category: "AICF", image: "", caption: "" });
  const load = () => client.get("/gallery").then(r => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await client.post("/admin/gallery", form);
    setForm({ title: "", category: "AICF", image: "", caption: "" }); load();
  };
  const del = async (id) => { if (confirm("Delete?")) { await client.delete(`/admin/gallery/${id}`); load(); } };

  return (
    <div className="grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-5">
        <form onSubmit={submit} className="border hairline bg-white p-6 space-y-4 sticky top-24">
          <div className="eyebrow">Add gallery image</div>
          <input className="form-input" placeholder="Image URL" value={form.image} onChange={(e) => setForm({...form, image: e.target.value})} required />
          <input className="form-input" placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required />
          <select className="form-input" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}>
            {["AICF","Chess events","Business","Speaking","Media","Companies"].map(c => <option key={c}>{c}</option>)}
          </select>
          <textarea className="form-input" rows="3" placeholder="Caption (optional)" value={form.caption} onChange={(e) => setForm({...form, caption: e.target.value})} />
          <button className="btn w-full justify-center">Add Image</button>
        </form>
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(g => (
          <div key={g.id} className="border hairline bg-white">
            <div className="aspect-square bg-[#ece7da]">
              <img src={g.image} alt={g.title} className="w-full h-full object-cover"/>
            </div>
            <div className="p-3">
              <div className="text-[12px] font-medium text-[#0a1628] truncate">{g.title}</div>
              <div className="text-[10px] text-[#6b6b6b]">{g.category}</div>
              <button onClick={() => del(g.id)} className="text-[10px] tracking-[0.14em] uppercase text-red-700 mt-2">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LeadsAdmin() {
  const [items, setItems] = useState([]);
  const load = () => client.get("/admin/leads").then(r => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => { await client.patch(`/admin/leads/${id}`, { status }); load(); };
  const del = async (id) => { if (confirm("Delete?")) { await client.delete(`/admin/leads/${id}`); load(); } };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="eyebrow">{items.length} leads</div>
      </div>
      {items.map(l => (
        <div key={l.id} className="border hairline bg-white p-6">
          <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
            <div>
              <div className="font-display text-lg text-[#0a1628]">{l.name}</div>
              <div className="text-[12px] text-[#6b6b6b]">{l.email} {l.phone && `· ${l.phone}`} {l.org && `· ${l.org}`}</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] tracking-[0.18em] uppercase bg-[#faf3e2] px-2 py-1">{l.type}</span>
              <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-1 ${l.status === "new" ? "bg-[#0a1628] text-[#faf8f3]" : "bg-[#ece7da]"}`}>{l.status}</span>
            </div>
          </div>
          {l.subject && <div className="text-[13px] font-medium text-[#0a1628] mb-1">{l.subject}</div>}
          <p className="text-[14px] text-[#2c2c2c] leading-relaxed whitespace-pre-wrap">{l.message}</p>
          <div className="text-[11px] text-[#6b6b6b] mt-3">{new Date(l.created_at).toLocaleString("en-IN")}</div>
          <div className="flex gap-2 mt-3 text-[10px] tracking-[0.14em] uppercase flex-wrap">
            {l.status !== "responded" && <button onClick={() => setStatus(l.id, "responded")} className="px-3 py-1.5 border border-[#0a1628]">Mark responded</button>}
            <a href={`mailto:${l.email}?subject=${encodeURIComponent("Re: " + (l.subject || "Your enquiry"))}`} className="px-3 py-1.5 border hairline">Reply ↗</a>
            <button onClick={() => del(l.id)} className="px-3 py-1.5 border hairline text-red-700">Delete</button>
          </div>
        </div>
      ))}
      {items.length === 0 && <div className="border hairline bg-white p-10 text-center text-[#6b6b6b]">No leads yet.</div>}
    </div>
  );
}


// ---------- Pending Approvals ----------

function PendingAdmin() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [status, setStatus] = useState("pending");

  const load = async () => {
    setBusy(true);
    try {
      const r = await client.get(`/admin/pending?status=${status}`);
      setItems(r.data.items || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await client.post(`/admin/aggregator/run-now`);
      setLastRun(r.data.counts);
      await load();
    } catch (e) {
      alert("Run failed: " + (e?.response?.data?.detail || e.message));
    } finally { setRunning(false); }
  };

  const approve = async (id) => {
    if (!window.confirm("Approve and publish this item?")) return;
    await client.post(`/admin/pending/${id}/approve`);
    await load();
  };
  const reject = async (id) => {
    await client.post(`/admin/pending/${id}/reject`);
    await load();
  };

  return (
    <div className="space-y-6">
      <div className="border hairline bg-[#0a1628] text-[#faf8f3] p-6 lg:p-8 flex flex-col lg:flex-row gap-6 justify-between">
        <div>
          <div className="eyebrow text-[#b8975d] mb-2">Live · News aggregator</div>
          <h3 className="font-display text-2xl mb-2">Auto-collected mentions from the web</h3>
          <p className="text-[13px] text-[#cfc6b3] leading-relaxed max-w-xl">
            Every day at <strong>10:00</strong> and <strong>19:00 IST</strong>, the aggregator pulls fresh mentions of Nitin Narang / AICF from Google News, FIDE, ChessBase India, Reddit and chess/business outlets, then filters them through an AI relevance check. Approved items go live at <em>nitinnarang.com/news</em>.
          </p>
          {lastRun && (
            <div className="text-[12px] mt-3 text-[#b8975d]">
              Last run · fetched {lastRun.fetched} · new {lastRun.new} · relevant {lastRun.relevant} · auto-rejected {lastRun.auto_rejected}
            </div>
          )}
        </div>
        <button data-testid="agg-run-now" onClick={runNow} disabled={running}
          className="bg-[#b8975d] text-[#0a1628] px-5 py-3 font-semibold text-[12px] tracking-[0.2em] uppercase hover:bg-[#cfa970] disabled:opacity-60 self-start">
          {running ? "Running…" : "⚡ Run aggregator now"}
        </button>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["pending", "approved", "rejected", "auto_rejected"].map(s => (
          <button key={s} data-testid={`agg-filter-${s}`}
            onClick={() => setStatus(s)}
            className={`px-4 py-2 text-[11px] tracking-[0.16em] uppercase border ${
              status === s ? "bg-[#0a1628] text-white border-[#0a1628]" : "border-[#d9d4c7] hover:border-[#0a1628]"
            }`}>
            {s.replace("_", " ")} {status === s && `(${items.length})`}
          </button>
        ))}
      </div>

      {busy && <div className="text-[13px] text-[#6b6b6b]">Loading…</div>}
      {!busy && items.length === 0 && (
        <div className="border hairline bg-white p-10 text-center text-[#6b6b6b]">
          No items in this state.
        </div>
      )}

      <div className="space-y-3">
        {items.map(it => (
          <div key={it.id} data-testid={`pending-item-${it.id}`}
            className="border hairline bg-white p-5 flex flex-col lg:flex-row gap-5">
            <div className="flex-1">
              <div className="flex items-center gap-3 text-[11px] tracking-[0.16em] uppercase mb-2">
                <span className="text-[#b8975d]">{it.source}</span>
                <span className="text-[#6b6b6b]">·</span>
                <span className="text-[#6b6b6b]">{it.date}</span>
                <span className="text-[#6b6b6b]">·</span>
                <span className="text-[#6b6b6b]">{it.category}</span>
                {it.relevance?.confidence !== undefined && (
                  <span className={`ml-2 px-2 py-0.5 border text-[10px] ${
                    it.relevance.confidence >= 0.8 ? "border-green-700 text-green-700" :
                    it.relevance.confidence >= 0.5 ? "border-amber-700 text-amber-700" :
                    "border-red-700 text-red-700"
                  }`}>
                    AI {Math.round(it.relevance.confidence * 100)}%
                  </span>
                )}
              </div>
              <a href={it.url} target="_blank" rel="noreferrer" className="font-display text-lg text-[#0a1628] hover:text-[#b8975d] block mb-2">
                {it.title}
              </a>
              <p className="text-[13px] text-[#4a4a4a] leading-relaxed mb-2 line-clamp-3">{it.excerpt}</p>
              {it.relevance?.reason && (
                <p className="text-[11px] text-[#6b6b6b] italic">AI note: {it.relevance.reason}</p>
              )}
            </div>
            <div className="flex lg:flex-col gap-2 flex-shrink-0">
              {it.status === "pending" && (
                <>
                  <button data-testid={`approve-${it.id}`} onClick={() => approve(it.id)}
                    className="bg-[#0a1628] text-white px-4 py-2 text-[11px] tracking-[0.18em] uppercase hover:bg-[#b8975d] hover:text-[#0a1628]">
                    ✓ Approve
                  </button>
                  <button data-testid={`reject-${it.id}`} onClick={() => reject(it.id)}
                    className="border hairline px-4 py-2 text-[11px] tracking-[0.18em] uppercase hover:border-red-700 hover:text-red-700">
                    ✗ Reject
                  </button>
                </>
              )}
              {it.status !== "pending" && (
                <span className="text-[11px] tracking-[0.16em] uppercase text-[#6b6b6b] px-4 py-2 border hairline">
                  {it.status}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
